'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// Type-only imports from the api trpc boundary — erased at build
import type { MembershipSummary, SessionUser, VerifyOtpResult } from '@slikerr/api/trpc';

export type { MembershipSummary, SessionUser, VerifyOtpResult };

// ---------------------------------------------------------------------------
// localStorage shape
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'slikerr_session';

interface StoredSession {
  userId: string;
  phone: string | null;
  isPlatformAdmin: boolean;
  activeBusinessId: string | null;
  activeBusinessName: string | null;
  activeBusinessRole: string | null;
  refreshToken: string;
}

function readStorage(): StoredSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStorage(session: StoredSession): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

// ---------------------------------------------------------------------------
// Impersonation (tranche G) — sessionStorage, deliberately NOT localStorage
// ---------------------------------------------------------------------------

/**
 * A platform admin acting as one business. Held in **sessionStorage** so it
 * dies with the tab: there is no refresh path for this token (the API mints an
 * access token and no refresh token), so a session that outlived the tab could
 * only ever come back already dead — or, worse, come back alive-looking in a
 * window the admin had forgotten about.
 */
export interface ImpersonationSession {
  businessId: string;
  businessName: string;
  accessToken: string;
  /** Epoch ms. DISPLAY ONLY — see `decodeJwtExpMs`. */
  expiresAt: number;
}

const IMPERSONATION_KEY = 'slikerr_impersonation';

/** Fallback window when the token carries no readable `exp`. */
const IMPERSONATION_FALLBACK_MS = 15 * 60 * 1000;

/**
 * Read `exp` out of a JWT payload for the countdown.
 *
 * **Display only.** There is no signature check here and there must never be
 * one: nothing on the client can verify this token, and every real decision
 * about it is the API's. A tampered `exp` buys nothing — the API still rejects
 * the token — and a missing one just falls back to +15 minutes.
 */
function decodeJwtExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Build a session from a freshly minted `admin.actAs` token. */
export function makeImpersonationSession(
  businessId: string,
  businessName: string,
  accessToken: string,
  now: number = Date.now(),
): ImpersonationSession {
  return {
    businessId,
    businessName,
    accessToken,
    expiresAt: decodeJwtExpMs(accessToken) ?? now + IMPERSONATION_FALLBACK_MS,
  };
}

function readImpersonation(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationSession;
    if (
      typeof parsed?.businessId !== 'string' ||
      typeof parsed?.accessToken !== 'string' ||
      typeof parsed?.expiresAt !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeImpersonation(session: ImpersonationSession): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));
  } catch {
    // Storage quota / disabled storage — the in-memory ref still works for
    // this page, and losing it on reload fails CLOSED.
  }
}

function clearImpersonation(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // Never let a storage failure break logout for everyone else.
  }
}

function clearStorage(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  // Both `logout()` and `_onRefreshFailed()` route through here, and the
  // second one is the reason this line exists: a failed refresh bounces to
  // /login, the admin signs back in IN THE SAME TAB, and without this they
  // would silently resume impersonating a business they had stopped watching.
  clearImpersonation();
}

// ---------------------------------------------------------------------------
// Context value types
// ---------------------------------------------------------------------------

interface AuthState {
  user: SessionUser | null;
  activeBusiness: MembershipSummary | null;
  /** Access token — in-memory only. null after reload until first tRPC call triggers refresh. */
  accessToken: string | null;
  /** Non-null while a platform admin is acting as a business. */
  impersonation: ImpersonationSession | null;
  /**
   * True after an impersonation ENDED BY ITSELF (countdown hit zero, or a 401
   * came back on an impersonated request). Kept separate from `impersonation`
   * so the banner can keep saying "you are back on your own account" after
   * the session it describes is gone. Silently unmounting is the one outcome
   * this feature must never have.
   */
  impersonationExpired: boolean;
}

interface AuthActions {
  /**
   * Called after verifyOtp (step 1) to store the unscoped access+refresh tokens
   * and user info, BEFORE calling selectBusiness. This ensures that the
   * selectBusiness call (a protectedProcedure) has a valid bearer token.
   * Does NOT write a full session to localStorage (business not selected yet).
   */
  storeVerifyOtpResult(result: VerifyOtpResult): void;

  /**
   * Called after selectBusiness returns the bid-scoped access token.
   * Writes the full session (user + business + refresh token) to localStorage.
   */
  login(result: VerifyOtpResult, activeMembership: MembershipSummary, accessToken: string): void;

  /**
   * Called for every platform admin at login (login/page.tsx routes them
   * here unconditionally, regardless of any business membership they also
   * hold). Mirrors `login` but writes null business fields — `refresh()`
   * re-derives `pa` from the live user (auth.service.ts:347-356), so this
   * session refreshes into another unscoped pa:true token indefinitely.
   * Additive only; does not change `login`'s behaviour.
   */
  loginPlatformAdmin(result: VerifyOtpResult): void;

  /** Clears all session data. Caller must have called auth.logout API first. */
  logout(): void;

  /** Switch active business (after selectBusiness API call). */
  setActiveBusiness(membership: MembershipSummary, accessToken: string): void;

  /** Returns current refresh token via a ref — stable across re-renders. */
  getRefreshToken(): string | null;

  /** Returns current access token via a ref — stable across re-renders.
   *  Use this in long-lived closures (e.g. the tRPC client) instead of reading
   *  auth.accessToken directly, which would capture a stale snapshot.
   *  Returns the IMPERSONATION token while one is active. */
  getAccessToken(): string | null;

  /** Begin acting as a business with a freshly minted `admin.actAs` token. */
  startImpersonation(session: ImpersonationSession): void;

  /** Replace the live impersonation token with a renewed one (same business). */
  renewImpersonation(accessToken: string): void;

  /** Deliberate exit — clears the session AND any expired notice. */
  stopImpersonation(): void;

  /** The session ended on its own: clear it, but leave the notice up. */
  expireImpersonation(): void;
}

/** Extended internal-only callbacks wired into the tRPC fetch layer. */
interface AuthInternals {
  /** Called when auth.refresh succeeds — persists BOTH rotated tokens. */
  _onRefreshed(accessToken: string, refreshToken: string): void;
  /** Called when auth.refresh fails — always clears the session; redirects to
   *  /login only when a session actually existed and we are not already there
   *  (issue #425 — see the implementation's comment). */
  _onRefreshFailed(): void;
  /** Ref-backed, so the tRPC fetch closure (created ONCE in useState) reads the
   *  live value instead of the `impersonation: null` it captured at mount. */
  _isImpersonating(): boolean;
  /** Called by the fetch wrapper when an impersonated request answers 401. */
  _onImpersonationExpired(): void;
}

type AuthContextValue = AuthState & AuthActions & AuthInternals;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    activeBusiness: null,
    accessToken: null,
    impersonation: null,
    impersonationExpired: false,
  });

  // Both tokens live in refs so long-lived closures (tRPC client, fetch wrapper)
  // always read the current value rather than a stale state snapshot.
  const refreshTokenRef = useRef<string | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  // The impersonation session ALSO lives in a ref, for the same reason the
  // tokens do: `makeAuthFetch`'s callbacks are built once and would otherwise
  // read the null captured at mount. Every writer below must keep the two in
  // step — a missed ref write is the guard silently switched off.
  const impersonationRef = useRef<ImpersonationSession | null>(null);

  // On mount: restore minimal session from localStorage so the auth guard
  // does not immediately redirect. The access token will be re-obtained via
  // the refresh wrapper on the first authed tRPC request.
  useEffect(() => {
    // Impersonation is restored FIRST and independently of the localStorage
    // session, and an already-expired key is DISCARDED rather than restored:
    // a dead session that still renders a live banner is exactly the "the UI
    // says acting as X" failure this feature exists to prevent.
    const imp = readImpersonation();
    if (imp && imp.expiresAt > Date.now()) {
      impersonationRef.current = imp;
      setState((prev) => ({ ...prev, impersonation: imp }));
    } else if (imp) {
      clearImpersonation();
    }

    const stored = readStorage();
    if (!stored) return;
    refreshTokenRef.current = stored.refreshToken;
    // accessToken stays null — will be refreshed on first tRPC call
    setState((prev) => ({
      ...prev,
      user: {
        id: stored.userId,
        phone: stored.phone,
        isPlatformAdmin: stored.isPlatformAdmin,
      },
      activeBusiness: stored.activeBusinessId
        ? {
            businessId: stored.activeBusinessId,
            businessName: stored.activeBusinessName ?? '',
            role: (stored.activeBusinessRole ?? 'client') as MembershipSummary['role'],
          }
        : null,
      accessToken: null,
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Store verifyOtp tokens in refs so that the *next* tRPC call (selectBusiness)
   * goes out with a valid bearer token. Does not write to localStorage.
   * The user is NOT yet considered "fully logged in" (no business selected).
   */
  const storeVerifyOtpResult = useCallback((result: VerifyOtpResult) => {
    refreshTokenRef.current = result.refreshToken;
    accessTokenRef.current = result.accessToken;
    // A new sign-in in this tab starts clean. Without this the very next call
    // (selectBusiness) would go out bearing a leftover impersonation token,
    // because getAccessToken() prefers it.
    impersonationRef.current = null;
    clearImpersonation();
    setState((prev) => ({
      ...prev,
      user: result.user,
      accessToken: result.accessToken,
      impersonation: null,
      impersonationExpired: false,
    }));
  }, []);

  const login = useCallback(
    (result: VerifyOtpResult, activeMembership: MembershipSummary, accessToken: string) => {
      refreshTokenRef.current = result.refreshToken;
      accessTokenRef.current = accessToken;
      writeStorage({
        userId: result.user.id,
        phone: result.user.phone,
        isPlatformAdmin: result.user.isPlatformAdmin,
        activeBusinessId: activeMembership.businessId,
        activeBusinessName: activeMembership.businessName,
        activeBusinessRole: activeMembership.role,
        refreshToken: result.refreshToken,
      });
      impersonationRef.current = null;
      clearImpersonation();
      setState({
        user: result.user,
        activeBusiness: activeMembership,
        accessToken,
        impersonation: null,
        impersonationExpired: false,
      });
    },
    [],
  );

  const loginPlatformAdmin = useCallback((result: VerifyOtpResult) => {
    refreshTokenRef.current = result.refreshToken;
    accessTokenRef.current = result.accessToken;
    writeStorage({
      userId: result.user.id,
      phone: result.user.phone,
      isPlatformAdmin: result.user.isPlatformAdmin,
      activeBusinessId: null,
      activeBusinessName: null,
      activeBusinessRole: null,
      refreshToken: result.refreshToken,
    });
    impersonationRef.current = null;
    clearImpersonation();
    setState({
      user: result.user,
      activeBusiness: null,
      accessToken: result.accessToken,
      impersonation: null,
      impersonationExpired: false,
    });
  }, []);

  const logout = useCallback(() => {
    refreshTokenRef.current = null;
    accessTokenRef.current = null;
    // clearStorage() drops the sessionStorage key; the REF is separate state
    // and TypeScript cannot notice it going stale.
    impersonationRef.current = null;
    clearStorage();
    setState({
      user: null,
      activeBusiness: null,
      accessToken: null,
      impersonation: null,
      impersonationExpired: false,
    });
  }, []);

  const setActiveBusiness = useCallback((membership: MembershipSummary, accessToken: string) => {
    accessTokenRef.current = accessToken;
    setState((prev) => {
      if (prev.user && refreshTokenRef.current) {
        writeStorage({
          userId: prev.user.id,
          phone: prev.user.phone,
          isPlatformAdmin: prev.user.isPlatformAdmin,
          activeBusinessId: membership.businessId,
          activeBusinessName: membership.businessName,
          activeBusinessRole: membership.role,
          refreshToken: refreshTokenRef.current,
        });
      }
      return { ...prev, activeBusiness: membership, accessToken };
    });
  }, []);

  const getRefreshToken = useCallback(() => refreshTokenRef.current, []);

  /**
   * The impersonation token WINS while one is live, but `accessTokenRef` is
   * deliberately left untouched by every impersonation action. The admin's own
   * token has to survive underneath, so leaving is one click and takes effect
   * on the very next request — no re-login, no refresh round trip.
   */
  const getAccessToken = useCallback(
    () => impersonationRef.current?.accessToken ?? accessTokenRef.current,
    [],
  );

  // ── Impersonation actions ────────────────────────────────────────────────

  const startImpersonation = useCallback((session: ImpersonationSession) => {
    impersonationRef.current = session;
    writeImpersonation(session);
    setState((prev) => ({ ...prev, impersonation: session, impersonationExpired: false }));
  }, []);

  const renewImpersonation = useCallback((accessToken: string) => {
    const current = impersonationRef.current;
    // Renewing something that is no longer live would resurrect a session the
    // admin has already left.
    if (!current) return;
    const next = makeImpersonationSession(current.businessId, current.businessName, accessToken);
    impersonationRef.current = next;
    writeImpersonation(next);
    setState((prev) => ({ ...prev, impersonation: next, impersonationExpired: false }));
  }, []);

  const stopImpersonation = useCallback(() => {
    impersonationRef.current = null;
    clearImpersonation();
    setState((prev) => ({ ...prev, impersonation: null, impersonationExpired: false }));
  }, []);

  const expireImpersonation = useCallback(() => {
    // Idempotent: the 1s countdown and a 401 can both arrive, and the second
    // one must not re-raise the notice after a deliberate exit.
    if (!impersonationRef.current) return;
    impersonationRef.current = null;
    clearImpersonation();
    setState((prev) => ({ ...prev, impersonation: null, impersonationExpired: true }));
  }, []);

  const _isImpersonating = useCallback(() => impersonationRef.current !== null, []);

  // Called by the auth-fetch wrapper when refresh succeeds.
  // MUST persist both tokens — the API rotates the refresh token on every use.
  const _onRefreshed = useCallback((accessToken: string, newRefreshToken: string) => {
    refreshTokenRef.current = newRefreshToken;
    accessTokenRef.current = accessToken;
    const stored = readStorage();
    if (stored) {
      writeStorage({ ...stored, refreshToken: newRefreshToken });
    }
    setState((prev) => ({ ...prev, accessToken }));
  }, []);

  // Called by the auth-fetch wrapper when refresh fails (token revoked / expired).
  //
  // The redirect exists to end a session that has gone bad: a 401 mid-dashboard
  // means the tokens are dead and the user must sign in again. It is NOT a
  // general handler for "some request returned 401", which is what the wrapper
  // actually calls it for — every `publicProcedure` on this client can answer
  // 401 as a NORMAL outcome (a wrong OTP code, an IAP assertion for an address
  // with no account), with no session involved anywhere.
  //
  // Unguarded, that difference was an INFINITE LOOP, not a cosmetic wrinkle
  // (issue #425, 2026-08-16). `window.location.href` is a hard navigation, so
  // on /login it reloaded /login, re-running the login page's mount effect,
  // which called iapSignIn, which 401'd, which landed back here — about three
  // cycles a second. It ran 17 times before the per-IP rate limiter started
  // answering 429 instead of 401 and broke the cycle: the limiter was the
  // circuit breaker, which is why the incident presented as a rate-limit
  // problem. Every IAP-allowlisted visitor without a Slikerr account hit it.
  //
  // Two independent guards. They are NOT redundant — they cover different
  // cases (a public 401 anywhere; a session that dies while on /login) and
  // each has its own test.
  const _onRefreshFailed = useCallback(() => {
    const hadSession = refreshTokenRef.current !== null || accessTokenRef.current !== null;
    refreshTokenRef.current = null;
    accessTokenRef.current = null;
    impersonationRef.current = null;
    clearStorage();
    setState({
      user: null,
      activeBusiness: null,
      accessToken: null,
      impersonation: null,
      impersonationExpired: false,
    });
    if (typeof window === 'undefined') return;
    // 1. No session existed, so there is nothing to end and nowhere to send
    //    anyone — this was a public call that happens to answer 401.
    if (!hadSession) return;
    // 2. Already on the sign-in page. Navigating there from there can only
    //    discard in-progress form state and re-run mount effects.
    if (window.location.pathname === '/login') return;
    window.location.href = '/login';
  }, []);

  const value: AuthContextValue = {
    ...state,
    storeVerifyOtpResult,
    login,
    loginPlatformAdmin,
    logout,
    setActiveBusiness,
    getRefreshToken,
    getAccessToken,
    startImpersonation,
    renewImpersonation,
    stopImpersonation,
    expireImpersonation,
    _onRefreshed,
    _onRefreshFailed,
    _isImpersonating,
    _onImpersonationExpired: expireImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAuth(): AuthState & AuthActions {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Internal hook — used only by TRPCProvider to wire the fetch layer callbacks. */
export function useAuthInternal(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthInternal must be used within AuthProvider');
  return ctx;
}
